import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injector, inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environment/environment';
import { TranslocoService } from '@jsverse/transloco';
import { errorCodeOf } from '@libs/errors/error-codes';
import { AuthService } from '@services/auth/auth.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { catchError, EMPTY, Observable, switchMap, throwError } from 'rxjs';

export const AuthInterceptor: HttpInterceptorFn = (
	request: HttpRequest<unknown>,
	next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
	const router = inject(Router);
	const platformId = inject(PLATFORM_ID);
	// AuthService is resolved lazily: inject() is not allowed inside catchError (NG0203)
	const injector = inject(Injector);
	const TOKEN_KEY = 'JWT_TOKEN';

	//console.log('🔐 Intercepting request to:', request.url);

	// Login/register/logout go without the stored token; their coded errors (e.g. TOO_MANY_REQUESTS) are
	// still translated so the screens show them in the active language
	if (
		request.url.includes('/auth/login') ||
		request.url.includes('/auth/register') ||
		request.url.includes('/auth/logout')
	) {
		return next(request).pipe(catchError((error) => throwError(() => translateApiError(error, injector))));
	}

	// Server-side render: there is no session there, so a private endpoint can only answer 401.
	// Skip it (the page renders its loading state and the browser loads the data after hydration)
	if (!isPlatformBrowser(platformId) && isPrivateApiCall(request.url)) {
		return EMPTY;
	}

	// Check if we are in browser before accessing localStorage
	let token: string | null = null;
	if (isPlatformBrowser(platformId)) {
		token = localStorage.getItem(TOKEN_KEY);
	}

	if (!token) {
		return next(request).pipe(catchError((error) => throwError(() => translateApiError(error, injector))));
	}

	// Verify token format
	if (!token.includes('.')) {
		if (isPlatformBrowser(platformId)) {
			router.navigate(['/login']);
		}
		return throwError(() => new Error('Invalid token'));
	}

	try {
		// Clone request and add headers
		const modifiedRequest = request.clone({
			withCredentials: false, // Only use credentials when necessary
			setHeaders: {
				Authorization: `Bearer ${token}`,
				Accept: 'application/json',
				...(!(request.body instanceof FormData) && {
					'Content-Type': 'application/json',
				}),
			},
		});

		// Debug headers
		const _headers = modifiedRequest.headers;
		/*console.log('📨 Request headers:', {
			Authorization: headers.get('Authorization')?.substring(0, 40),
			ContentType: headers.get('Content-Type'),
			Accept: headers.get('Accept'),
		});*/

		return next(modifiedRequest).pipe(
			catchError((error: HttpErrorResponse) => {
				translateApiError(error, injector);
				// Failed requests reach Sentry through httpClientIntegration (5xx only), not from here: it duplicated them

				// The account has two-step verification and this session never passed it: the code step is
				// only reachable by logging in again (the login answers with the MFA challenge)
				if (error.status === 403 && errorCodeOf(error.error) === 'MFA_REQUIRED') {
					endSessionForMfa(injector, router);
					return throwError(() => error);
				}
				if (error.status === 401) {
					const isPasswordChangeError = request.url.includes('/password');
					const isRefreshRequest = request.url.includes('/auth/refresh');

					// The refresh request itself is handled by AuthService.refreshToken (it logs out once)
					if (!isPasswordChangeError && !isRefreshRequest) {
						// Parallel 401s all wait for the same refresh (AuthService shares the one in flight)
						// and retry with the new token; if it fails, they all fail and the session ends once
						const retryWithCurrentToken = () =>
							next(
								modifiedRequest.clone({
									setHeaders: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
								}),
							);

						// Sent with a token that another refresh has already replaced: just retry with the new one
						const currentToken = localStorage.getItem(TOKEN_KEY);
						if (currentToken && currentToken !== token) {
							return retryWithCurrentToken();
						}

						return injector
							.get(AuthService)
							.refreshToken()
							.pipe(switchMap(() => retryWithCurrentToken()));
					}
				}

				return throwError(() => error);
			}),
		);
	} catch (error) {
		return throwError(() => error);
	}
};

/** One logout for a burst of parallel MFA_REQUIRED answers */
let mfaLogoutInProgress = false;

function endSessionForMfa(injector: Injector, router: Router): void {
	if (mfaLogoutInProgress) return;
	mfaLogoutInProgress = true;
	const returnUrl = router.url;
	const transloco = injector.get(TranslocoService);
	injector
		.get(MessageAlertService)
		.showMessage('warn', transloco.translate('common.labels.warning'), transloco.translate('errors.MFA_REQUIRED'));
	injector
		.get(AuthService)
		.logout()
		.subscribe({
			complete: () => {
				mfaLogoutInProgress = false;
				// logout() opens /login; keep where the user was so the login brings them back
				if (AuthService.isSafeReturnUrl(returnUrl)) {
					router.navigate(['/login'], { queryParams: { returnUrl } });
				}
			},
		});
}

/** Backend endpoints reachable without a session (their controllers mark them @Public) */
const PUBLIC_API = /\/(platforms\/public|products\/public|health|auth)(\/|$|\?)/;

function isPrivateApiCall(url: string): boolean {
	return url.includes(environment.pathBackend) && !PUBLIC_API.test(url);
}

/**
 * The backend sends a stable `code` (and `params`) with the errors users see. When `errors.<code>` is
 * translated, its text replaces `error.error.message`: every screen that shows the backend message
 * gets it in the active language without changing each call site. Unknown codes keep the message.
 */
function translateApiError(error: unknown, injector: Injector): unknown {
	const body = (error as HttpErrorResponse)?.error as {
		code?: string;
		params?: Record<string, unknown>;
		message?: string;
	};
	if (!body || typeof body !== 'object' || !body.code) return error;
	const transloco = injector.get(TranslocoService);
	const key = `errors.${body.code}`;
	const text = transloco.translate(key, body.params ?? {});
	if (text && text !== key) body.message = text;
	return error;
}
